import React, { useState } from 'react';
import { ExternalLink, Code, Smartphone, Globe, Wrench, Star, Users, Zap } from 'lucide-react';

interface Tool {
  name: string;
  description: string;
  url: string;
  category: string;
  featured?: boolean;
}

const UsefulTools: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('business');

  const businessTools: Tool[] = [
    {
      name: 'Hostinger',
      description: 'Cheapest domain registration and web hosting',
      url: 'https://hostinger.com',
      category: 'business',
      featured: true
    },
    {
      name: 'Nameflix',
      description: 'AI-powered startup name generator',
      url: 'https://nameflix.com',
      category: 'business'
    },
    {
      name: 'Canva',
      description: 'Design logos, presentations, and marketing materials',
      url: 'https://canva.com',
      category: 'business'
    },
    {
      name: 'Stripe',
      description: 'Accept payments online with ease',
      url: 'https://stripe.com',
      category: 'business'
    },
    {
      name: 'Polar',
      description: 'Payment gateway for regions where Stripe is unavailable',
      url: 'https://polar.sh',
      category: 'business',
      featured: true
    },
    {
      name: 'Mailchimp',
      description: 'Email marketing and automation platform',
      url: 'https://mailchimp.com',
      category: 'business'
    },
    {
      name: 'Typeform',
      description: 'Beautiful forms and surveys for customer feedback',
      url: 'https://typeform.com',
      category: 'business'
    }
  ];

  const coderTools: Tool[] = [
    {
      name: 'Cursor',
      description: 'AI-powered code editor for refactoring and development',
      url: 'https://cursor.sh',
      category: 'coders',
      featured: true
    },
    {
      name: 'GitHub Copilot',
      description: 'AI autocomplete for faster coding',
      url: 'https://github.com/features/copilot',
      category: 'coders',
      featured: true
    },
    {
      name: 'Replit AI',
      description: 'Full-stack development with AI assistance',
      url: 'https://replit.com',
      category: 'coders',
      featured: true
    },
    {
      name: 'Windsurf',
      description: 'Agentic AI development environment',
      url: 'https://windsurf.ai',
      category: 'coders',
      featured: true
    },
    {
      name: 'Devin',
      description: 'AI team developer for complex projects',
      url: 'https://devin.ai',
      category: 'coders',
      featured: true
    }
  ];

  const nonCoderTools: Tool[] = [
    {
      name: 'Lovable',
      description: 'AI + NoCode platform for building apps',
      url: 'https://lovable.dev',
      category: 'non-coders',
      featured: true
    },
    {
      name: 'Bolt',
      description: 'Versatile app builder with AI assistance',
      url: 'https://bolt.new',
      category: 'non-coders',
      featured: true
    },
    {
      name: 'v0',
      description: 'Generate UI components from text prompts',
      url: 'https://v0.dev',
      category: 'non-coders',
      featured: true
    },
    {
      name: 'Replit AI',
      description: 'AI assistant mode for beginners',
      url: 'https://replit.com',
      category: 'non-coders',
      featured: true
    },
    {
      name: 'Canva Code',
      description: 'Simple app creation for non-technical users',
      url: 'https://canva.com/apps',
      category: 'non-coders',
      featured: true
    }
  ];

  const mobileDevTools: Tool[] = [
    {
      name: 'FlutterFlow',
      description: 'Low-code mobile app development platform',
      url: 'https://flutterflow.io',
      category: 'mobile',
      featured: true
    },
    {
      name: 'Appy Pie',
      description: 'No-code mobile app generator',
      url: 'https://appypie.com',
      category: 'mobile',
      featured: true
    },
    {
      name: 'Glide',
      description: 'Data-driven mobile app builder',
      url: 'https://glideapps.com',
      category: 'mobile',
      featured: true
    },
    {
      name: 'Firebase Studio',
      description: 'Gemini-based mobile development',
      url: 'https://firebase.google.com',
      category: 'mobile',
      featured: true
    },
    {
      name: 'rork_app',
      description: 'Prompt-to-TestFlight mobile app creation',
      url: 'https://rork.app',
      category: 'mobile',
      featured: true
    }
  ];

  const webDevTools: Tool[] = [
    {
      name: 'v0',
      description: 'AI UI generation for web development',
      url: 'https://v0.dev',
      category: 'web',
      featured: true
    },
    {
      name: 'Replit',
      description: 'Full-stack web development platform',
      url: 'https://replit.com',
      category: 'web',
      featured: true
    },
    {
      name: 'Lovable',
      description: 'AI-powered web app development',
      url: 'https://lovable.dev',
      category: 'web',
      featured: true
    },
    {
      name: 'Bolt',
      description: 'Web development with Supabase integration',
      url: 'https://bolt.new',
      category: 'web',
      featured: true
    },
    {
      name: 'Cursor',
      description: 'Advanced code assistance for web development',
      url: 'https://cursor.sh',
      category: 'web',
      featured: true
    }
  ];

  const categories = [
    { id: 'business', label: 'Business Tools', icon: Wrench, tools: businessTools },
    { id: 'coders', label: 'For Coders', icon: Code, tools: coderTools },
    { id: 'non-coders', label: 'For Non-Coders', icon: Users, tools: nonCoderTools },
    { id: 'mobile', label: 'Mobile Dev', icon: Smartphone, tools: mobileDevTools },
    { id: 'web', label: 'Web Dev', icon: Globe, tools: webDevTools }
  ];

  const activeTools = categories.find(cat => cat.id === activeCategory)?.tools || [];

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="animate-fade-in mb-8">
          <h1 className="text-3xl font-bold text-startup-navy mb-2 flex items-center">
            <Wrench className="w-8 h-8 mr-3 text-startup-teal" />
            Useful Tools 🛠️
          </h1>
          <p className="text-startup-gray">
            Curated tools and resources to help you build and grow your startup
          </p>
        </div>

        {/* Category Navigation */}
        <div className="flex flex-wrap gap-2 mb-8 animate-fade-in">
          {categories.map((category, index) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center space-x-2 px-4 py-3 rounded-xl transition-all duration-300 animate-fade-in ${
                activeCategory === category.id
                  ? 'bg-startup-teal text-white shadow-lg'
                  : 'bg-white text-startup-gray hover:text-startup-navy hover:bg-startup-slate shadow-neumorphic'
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <category.icon className="w-5 h-5" />
              <span className="font-medium">{category.label}</span>
            </button>
          ))}
        </div>

        {/* Tools Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {activeTools.map((tool, index) => (
            <div
              key={tool.name}
              className={`group relative bg-white rounded-2xl p-6 shadow-neumorphic hover:shadow-xl transition-all duration-300 hover:scale-[1.02] animate-fade-in ${
                tool.featured ? 'ring-2 ring-startup-gold/20' : ''
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {tool.featured && (
                <div className="absolute -top-2 -right-2 bg-startup-gold text-white rounded-full p-2">
                  <Star className="w-4 h-4" />
                </div>
              )}
              
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-startup-navy mb-2 group-hover:text-startup-teal transition-colors">
                    {tool.name}
                  </h3>
                  <p className="text-startup-gray text-sm leading-relaxed">
                    {tool.description}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {tool.featured && (
                    <span className="bg-startup-gold/10 text-startup-gold px-2 py-1 rounded-full text-xs font-medium">
                      Top Pick
                    </span>
                  )}
                </div>
                
                <a
                  href={tool.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 bg-startup-teal text-white px-4 py-2 rounded-lg hover:bg-startup-teal/90 transition-colors group-hover:scale-105"
                >
                  <span className="text-sm font-medium">Visit</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Pro Tips Section */}
        <div className="mt-12 card-startup animate-fade-in">
          <div className="flex items-center mb-6">
            <Zap className="w-6 h-6 mr-3 text-startup-gold" />
            <h2 className="text-2xl font-semibold text-startup-navy">Pro Tips</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-startup-slate rounded-xl p-4">
              <h3 className="font-semibold text-startup-navy mb-2">🚀 For Beginners</h3>
              <p className="text-startup-gray text-sm">
                Start with no-code tools like Lovable or Bolt to validate your idea quickly, then consider learning to code with Cursor or Replit AI.
              </p>
            </div>
            
            <div className="bg-startup-slate rounded-xl p-4">
              <h3 className="font-semibold text-startup-navy mb-2">💰 Budget-Friendly</h3>
              <p className="text-startup-gray text-sm">
                Many tools offer free tiers. Start with Hostinger for domains ($0.99/year), use Canva's free plan, and leverage free GitHub Copilot trials.
              </p>
            </div>
            
            <div className="bg-startup-slate rounded-xl p-4">
              <h3 className="font-semibold text-startup-navy mb-2">📱 Mobile First</h3>
              <p className="text-startup-gray text-sm">
                If building mobile apps, FlutterFlow and Glide are excellent starting points. They generate real native apps without coding.
              </p>
            </div>
            
            <div className="bg-startup-slate rounded-xl p-4">
              <h3 className="font-semibold text-startup-navy mb-2">🔥 AI-Powered</h3>
              <p className="text-startup-gray text-sm">
                Embrace AI tools like v0 for UI design, Cursor for coding, and Nameflix for branding. They'll 10x your productivity.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsefulTools;
